import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import { alpha, Box, ButtonBase, List, Typography, type Theme } from '@mui/material';
import i18next from 'i18next';
import { useMemo, useState } from 'react';
import { LanguageType } from '../../../../@types';
import { useAvailableLanguages } from '../../../../hooks/useAvailableLanguages';
import { useLanguage } from '../../../../localization/LanguageInstanceProvider';
import NeobetCompactSidebarButton from './NeobetCompactSidebarButton';
import NeobetSidebarListItem from './NeobetSidebarListItem';

const styles = {
  section: {
    width: 1,
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: 'neutral.600',
  },
  sectionHeader: (isExpanded: boolean) => (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    width: 1,
    minHeight: 40,
    justifyContent: 'flex-start',
    gap: 1,
    px: 1.5,
    py: 1.125,
    color: theme.palette.text.primary,
    textAlign: 'left',
    backgroundColor: isExpanded ? 'neutral.500' : 'neutral.600',
    ...(!isExpanded && {
      '&:hover': {
        backgroundColor: 'neutral.500',
      },
    }),
  }),
  sectionHeaderIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    flexShrink: 0,
    color: 'text.primary',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  sectionHeaderTitle: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '20px',
  },
  sectionToggleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    flexShrink: 0,
    ml: 'auto',
    color: 'secondary.100',
  },
  list: {
    py: 0,
  },
  checkIcon: (theme: Theme) => ({
    ml: 'auto',
    flexShrink: 0,
    fontSize: 18,
    color: 'primary.600',
    filter: `drop-shadow(0 1px 4px ${alpha(theme.palette.common.black, 0.24)})`,
  }),
} as const;

type SidebarLanguageSectionProps = {
  isCompact?: boolean;
  isActive?: boolean;
};

const SidebarLanguageSection = ({ isCompact = false, isActive = true }: SidebarLanguageSectionProps) => {
  const { setSelectedLanguage } = useLanguage();
  const availableLanguages = useAvailableLanguages();
  const [isExpanded, setIsExpanded] = useState(false);
  const currentLanguage = i18next.language as LanguageType | undefined;

  const value = useMemo(() => {
    const fallbackValue = availableLanguages[0]?.key ?? 'en';
    const selectedLanguage = currentLanguage ?? fallbackValue;

    return availableLanguages.some((language) => language.key === selectedLanguage) ? selectedLanguage : fallbackValue;
  }, [availableLanguages, currentLanguage]);

  const selectedLanguageLabel = availableLanguages.find((language) => language.key === value)?.label ?? value;

  const languageList = (
    <List sx={styles.list}>
      {availableLanguages.map((language) => {
        const isSelected = language.key === value;

        return (
          <NeobetSidebarListItem
            key={language.key}
            label={language.label}
            isSelected={isSelected}
            selectedVariant='solid'
            icon={null}
            endIcon={isSelected ? <CheckRoundedIcon sx={styles.checkIcon} /> : null}
            onClick={() => {
              setSelectedLanguage(language.key);
              setIsExpanded(false);
            }}
          />
        );
      })}
    </List>
  );

  if (!availableLanguages.length) {
    return null;
  }

  if (isCompact) {
    return (
      <NeobetCompactSidebarButton
        aria-label={selectedLanguageLabel}
        testId='neobet-sidebar-language-section'
        flyoutTestId='neobet-sidebar-language-section-flyout'
        flyoutContent={isActive ? languageList : undefined}
      >
        <LanguageRoundedIcon fontSize='small' />
      </NeobetCompactSidebarButton>
    );
  }

  return (
    <Box sx={styles.section} data-testid='neobet-sidebar-language-section'>
      <ButtonBase
        onClick={() => setIsExpanded((expanded) => !expanded)}
        sx={styles.sectionHeader(isExpanded)}
        aria-label={selectedLanguageLabel}
        aria-expanded={isExpanded}
      >
        <Box sx={styles.sectionHeaderIcon}>
          <LanguageRoundedIcon fontSize='small' />
        </Box>
        <Typography variant='h5' sx={styles.sectionHeaderTitle}>
          {selectedLanguageLabel}
        </Typography>
        <Box component='span' sx={styles.sectionToggleIcon} aria-hidden='true'>
          {isExpanded ? <ExpandLessRoundedIcon fontSize='small' /> : <ExpandMoreRoundedIcon fontSize='small' />}
        </Box>
      </ButtonBase>

      {isExpanded ? languageList : null}
    </Box>
  );
};

export default SidebarLanguageSection;
